import anime, { AnimeInstance, AnimeParams } from "animejs";
import { ScrollDirection, ScrollHandler } from "../classes/scroll-manager";
import SceneManager from "../classes/scene-manager";
import { elementHeight, getEl } from "../helpers";
import App from "../index";


export interface SpotlightAnimation
{
	anim: AnimeInstance,
	mouseX: number,
	mouseY: number,
	mouseMoveDuringIntro: boolean,
	elWidth: number,
	elHeight: number,
	elLeft: number,
	elTop: number,
	smoothSpeed: number,
	mouseOver: boolean,
	onEnteredView: () => any | null,
	onExitedView: () => any | null,
	onMouseMove: MouseMoveCallback,
	el: Element,
}

export type MouseMoveCallback =  ((anim:SpotlightAnimation, delta: number) => any) | false;

export default class SpotlightAnimations
{
	static EXTRA_FRAMES_AFTER_MOUSEOUT: number = 120;
	static VISIBILITY_THRESHOLD: number = 0.75;

	public app:App;

	private lastDelta: number = 0;
	private framesRemaining: number = SpotlightAnimations.EXTRA_FRAMES_AFTER_MOUSEOUT;
	private readonly observer: IntersectionObserver|null = null;


	public isFrameRequested = false;

	public animations: SpotlightAnimation[] = [];



	constructor(app: App) {
		this.app = app;

		this.onAnimationFrame = this.onAnimationFrame.bind(this);
		this.onResize = this.onResize.bind(this);

		if ('IntersectionObserver' in window &&
			'IntersectionObserverEntry' in window &&
			'intersectionRatio' in window.IntersectionObserverEntry.prototype)
		{
			this.observer = new IntersectionObserver(
				this.onSpotlightIntersection.bind(this),
				{
					threshold: SpotlightAnimations.VISIBILITY_THRESHOLD,
				}
			);
		}
		else
		{
			console.warn("IntersectionObservers are not supported on this browser. Spotlight intro animations will not play.");
		}

		window.addEventListener('resize', this.onResize);
	}

	public addSpotlightAnimation(introanim: AnimeParams, spotlightElement: string|Element, smoothingTime: number, mouseMovedCallback: MouseMoveCallback, onEnteredViewCallback: () => any = null, mouseMoveDuringIntro: boolean = false)	{
		introanim.autoplay = false;
		const thisAnim: SpotlightAnimation = {
			anim: anime(introanim),
			onEnteredView: onEnteredViewCallback,
			onExitedView: () => {},
			mouseX: 0,
			mouseY: 0,
			mouseOver: false,
			onMouseMove: mouseMovedCallback,
			smoothSpeed: 1 / smoothingTime,
			elHeight: 1,
			elWidth: 1,
			elLeft: 0,
			elTop: 0,
			mouseMoveDuringIntro: mouseMoveDuringIntro,
			el: null,
		};

		const el: Element = spotlightElement instanceof Element ? spotlightElement : getEl(spotlightElement);
		if (!el) return false;

		thisAnim.el = el;

		if (thisAnim.onMouseMove) {
			el.addEventListener("mousemove", (e: MouseEvent) => {
				thisAnim.mouseOver = true;

				//@ts-ignore
				const bounds: DOMRect = e.currentTarget.getBoundingClientRect();

				thisAnim.mouseX = e.clientX - bounds.left;
				thisAnim.mouseY = e.clientY - bounds.top;
				thisAnim.elWidth = bounds.width;
				thisAnim.elHeight = bounds.height;
				this.framesRemaining = SpotlightAnimations.EXTRA_FRAMES_AFTER_MOUSEOUT;
				this.askForFrame();
				e.stopPropagation();

			});

			let mouseouttimer = 0;
			el.addEventListener("mouseout", (e: MouseEvent) => {
				clearTimeout(mouseouttimer);
				mouseouttimer = setTimeout(() => {
					thisAnim.mouseOver = false;
				}, 5000);
				this.askForFrame();
			});
		}

		if (this.observer)
		{
			this.observer.observe(el);
		}

		this.animations.push(thisAnim);

		this.onResize();
	}

	public askForFrame()
	{
		if (!this.isFrameRequested) {
			this.lastDelta = 0;
			this.framesRemaining = SpotlightAnimations.EXTRA_FRAMES_AFTER_MOUSEOUT;
			requestAnimationFrame(this.onAnimationFrame);
			this.isFrameRequested = true;
		}
	}

	public onAnimationFrame(delta: number)
	{
		if (this.framesRemaining <= 0)
		{
			this.isFrameRequested = false;
		}
		this.framesRemaining--;

		if (this.lastDelta == 0) this.lastDelta = delta;
		let deltaFrac = (delta-this.lastDelta);

		for (let i = 0; i < this.animations.length; i++) {
			if (this.animations[i].mouseOver && this.animations[i].onMouseMove)
			{
				//Dont do MouseMove effect until the intro animation has finished.
				if (!this.animations[i].mouseMoveDuringIntro && this.animations[i].anim && !this.animations[i].anim.completed) continue;

				//@ts-ignore
				this.animations[i].onMouseMove(this.animations[i], deltaFrac);
			}
		}

		if (this.isFrameRequested) requestAnimationFrame(this.onAnimationFrame);
		this.lastDelta = delta;
	}

	public onSpotlightIntersection(entries: IntersectionObserverEntry[], observer: IntersectionObserver)
	{
		entries.forEach((entry) => {
			if (entry.isIntersecting) {

				for (let i= 0; i < this.animations.length; i++) {

					if ( this.animations[i].el == entry.target)	{
						if (this.animations[i].anim && !this.animations[i].anim.completed) this.animations[i].anim.play();
						if (this.animations[i].onEnteredView != null) this.animations[i].onEnteredView();
					}
				}

				observer.unobserve(entry.target);
			}
		});
	}

	public onResize()
	{
		//This is not really needed now that we are calling
		//getBoundingClientRect in the mousemove event
		//But it's left here for completeness.

		for (let i = 0; i < this.animations.length; i++)
		{
			if (!this.animations[i].el) continue;

			const bounds = this.animations[i].el.getBoundingClientRect();
			this.animations[i].elWidth = bounds.width;
			this.animations[i].elHeight = bounds.height;
		}
	}
}
